<app-page>
    <app-breadcrumbs [crumbs]="crumbs"></app-breadcrumbs>

    <app-checkpoint-header [checkpoint]="checkpoint()" [platform]="platform()" [postponedBy]="postponedBy()" [results]="checkpointResults()">
        <button (click)="queueImmediately()"
                [class.queue-failed]="failedPush()"
                [class.queue-success]="successfulPush()"
                actions
                mat-stroked-button
                matTooltip="Zet de checkpoint met hoge prioriteit in de wachtrij"
                type="button">
            @if (successfulPush()) {
                <i class="fa-regular fa-check me-1"></i> In de wachtrij
            } @else if (failedPush()) {
                <i class="fa-regular fa-xmark me-1"></i> Mislukt
            } @else {
                <i class="fa-solid fa-play me-1"></i> Nu uitvoeren
            }
        </button>
        <!-- TODO(design): "Nu hervatten" has no endpoint of its own; the API lifts a postponement when a comment without hours is saved, so this focuses the comment field. -->
        @if (formCreateComment()) {
            <button (click)="focusComment()" mat-stroked-button resume type="button">Nu hervatten</button>
        }
    </app-checkpoint-header>

    <div class="row g-4 align-items-start">
        <div class="col-12 col-xl-8">
            <app-section [flush]="true" [meta]="stripSummary()" layout="stacked" title="Resultaten">
                @if (strip().length) {
                    <div class="strip">
                        <app-check-strip [items]="strip()" size="lg"></app-check-strip>
                        <div class="d-flex justify-content-between ui-faint strip-legend"><span>Ouder</span><span>Nu</span></div>
                    </div>
                }
                <app-data-list columns="72px 120px 84px 84px 84px 72px minmax(0, 1fr)" gap="12px" minWidth="680px">
                    <div app-data-row header>
                        <span>Tijdstip</span>
                        <span>Status</span>
                        <span>Statuscode</span>
                        <span class="text-end" matTooltip="Tijd tussen het geplande tijdstip en het einde van de check, inclusief de wachtrij">Uitgevoerd</span>
                        <span class="text-end">Reactietijd</span>
                        <span class="text-end">Grootte</span>
                        <span>Probleem</span>
                    </div>
                    @for (group of resultGroups(); track group.day) {
                        <app-day-group>{{ group.day }}</app-day-group>
                        @for (row of group.rows; track row.result.id) {
                            <a [routerLink]="['/', Routenames.seire, platformId, 'overview', checkpoint().id, row.result.id]"
                               [tone]="row.status.tone === 'error' ? 'error' : null"
                               app-data-row
                               dense>
                                <span class="ui-mono num">{{ row.result.finish_date ?? row.result.start_date | date: 'HH:mm' }}</span>
                                <app-status-dot [tone]="row.status.tone">{{ row.status.label }}</app-status-dot>
                                <span class="ui-mono num">{{ row.result.response_status_code ?? '—' }}</span>
                                <span class="ui-mono num text-end">{{ (row.result.finish_date | msBetween: row.result.start_date) || '—' }}</span>
                                <span class="ui-mono num text-end">{{ row.result.duration ? (row.result.duration | duration) : '—' }}</span>
                                <span class="ui-mono num text-end">{{ row.result.finish_date ? (row.result.size | bytesPipe) : '—' }}</span>
                                <span [class.ui-error]="row.status.tone === 'error'" [class.ui-warn]="row.status.tone === 'warn'" [title]="row.result.result ?? ''" class="problem ui-ellipsis">{{ row.problem }}</span>
                            </a>
                        }
                    } @empty {
                        <app-empty-state>
                            @if (checkpointResults() === null) {
                                Aan het laden…
                            } @else {
                                Geen checkpointresultaten gevonden
                            }
                        </app-empty-state>
                    }
                </app-data-list>
                <!-- TODO(design): "Meer laden" needs a paginated results endpoint; the API returns the last 50 results. -->
            </app-section>
        </div>

        @if (checkpointComments()) {
            <div class="col-12 col-xl-4 d-flex flex-column side">
                @if (formCreateComment()) {
                    <app-section [formGroup]="formCreateComment()" icon="pause" layout="stacked" title="Opschorten">
                        <div class="d-flex flex-column gap-3">
                            <p class="ui-help m-0">Tijdelijk geen meldingen, bijvoorbeeld tijdens onderhoud.</p>
                            @if (isPostponed()) {
                                <app-info-banner tone="warn">
                                    Opgeschort tot <b>{{ postponedTill() }}</b>. Kies een nieuwe duur om te verlengen.
                                </app-info-banner>
                            }
                            <app-field label="Duur">
                                <app-filter-chips (valueChange)="chooseDuration($event)" [options]="durationOptions" [value]="durationChoice()"></app-filter-chips>
                                <!-- TODO(design): "Tot morgen 09:00" is left out; the API postpones by whole hours from now (postpone_hours, max 72). -->
                            </app-field>
                            @if (durationChoice() === 'other') {
                                <app-field for="postpone_hours" label="Aantal uur">
                                    <mat-form-field>
                                        <input formControlName="postpone_hours" id="postpone_hours" matInput max="72" min="1" name="postpone_hours" type="number">
                                        <span class="ui-faint" matTextSuffix>uur</span>
                                        <mat-hint>Maximaal 72 uur.</mat-hint>
                                        <mat-error [control]="formCreateComment().get('postpone_hours')"></mat-error>
                                    </mat-form-field>
                                </app-field>
                            }
                            <app-field for="postpone_reason" label="Reden">
                                <mat-form-field>
                                    <textarea formControlName="comment" id="postpone_reason" matInput name="comment" placeholder="Bijv. onderhoud aan de server" rows="3"></textarea>
                                    <mat-error [control]="formCreateComment().get('comment')"></mat-error>
                                </mat-form-field>
                            </app-field>
                            <button (click)="save(formCreateComment())" [disabled]="!formCreateComment().value.postpone_hours" class="w-100" mat-flat-button type="submit">
                                {{ isPostponed() ? 'Verlengen' : 'Opschorten' }}
                            </button>
                        </div>
                    </app-section>
                }

                <app-section icon="comment" layout="stacked" title="Opmerkingen">
                    <div class="d-flex flex-column gap-3">
                        @for (checkpointComment of checkpointComments(); track checkpointComment.id) {
                            <div class="comment">
                                <div class="d-flex align-items-center gap-2 comment-head">
                                    <b>{{ checkpointComment?.user?.name }}</b>
                                    @if (checkpointComment.postpone_hours) {
                                        <app-status-badge size="sm" tone="warn" variant="tag">Opgeschort · {{ checkpointComment.postpone_hours }} uur</app-status-badge>
                                    }
                                    <span class="ms-auto ui-faint">{{ checkpointComment.created_at | codaltDate:'EEE d MMM yyyy HH:mm' }}</span>
                                    @if (+LocalStorage.getUser()?.id === +checkpointComment.user_id) {
                                        <button (click)="deleteComment($event, checkpointComment)" aria-label="Verwijderen" class="delete" mat-icon-button matTooltip="Verwijderen" type="button">
                                            <i class="fa-regular fa-trash-can"></i>
                                        </button>
                                    }
                                </div>
                                <div class="comment-text">{{ checkpointComment.comment }}</div>
                            </div>
                        } @empty {
                            <span class="ui-faint ui-help">Nog geen opmerkingen.</span>
                        }
                        @if (formCreateComment()) {
                            <div class="d-flex gap-2 align-items-start">
                                <app-field class="flex-grow-1">
                                    <mat-form-field>
                                        <!-- Enter is a new line (pasted stack traces keep theirs), Ctrl/Cmd + Enter places the comment -->
                                        <textarea #newCommentInput (keydown.control.enter)="placeComment()" (keydown.meta.enter)="placeComment()" (ngModelChange)="newComment.set($event)" [ngModel]="newComment()" aria-label="Opmerking toevoegen" matInput placeholder="Opmerking toevoegen" rows="3"></textarea>
                                        @if (isPostponed()) {
                                            <mat-hint>Let op: een opmerking plaatsen heft de opschorting op.</mat-hint>
                                        } @else {
                                            <mat-hint>Ctrl + Enter plaatst de opmerking.</mat-hint>
                                        }
                                    </mat-form-field>
                                </app-field>
                                <button (click)="placeComment()" [disabled]="!newComment().trim()" class="place" mat-stroked-button type="button">Plaatsen</button>
                            </div>
                        }
                    </div>
                </app-section>
            </div>
        }
    </div>
</app-page>
